import { toTimestampMs } from 'utils/dates/time.utils';

/** @typedef {import('../types/UsgsTypes').GraphDataPoint} GraphDataPoint */
/** @typedef {import('../types/UsgsTypes').GraphDatasetMeta} GraphDatasetMeta */

/**
 * Resolve a datasheet record id from ECharts event `params` using dataset metadata.
 *
 * The function attempts multiple lookup strategies:
 * - use `params.value[0]` or `params.axisValue` as a timestamp and match against
 *   `datasetMeta.byTimestampMap` (supporting numeric and string keys)
 * - fall back to scanning `datasetMeta.data` for a data point whose `ts` matches
 *   the resolved timestamp
 * - fall back to `params.dataIndex` when timestamp matching is unavailable
 *
 * @param {any} params - Event params provided by ECharts (may contain `axisValue`, `value`, `dataIndex`, etc.)
 * @param {GraphDatasetMeta | Record<string, any>} datasetMeta - Dataset `_meta` attached to the chart dataset (may include `byTimestampMap` and `data`)
 * @returns {number | string | null} Returns the record id if found, otherwise `null`.
 */
export const resolveDataRowIdFromParams = (params, datasetMeta) => {
  if (!params || !datasetMeta) return null;
  try {
    const timestampRaw = Array.isArray(params.value) ? params.value[0] : params.axisValue;
    const timestampNum = toTimestampMs(timestampRaw);
    const hasTimestamp = Number.isFinite(timestampNum) && !Number.isNaN(timestampNum);

    const byTimestampMap = datasetMeta?.byTimestampMap;
    if (hasTimestamp && byTimestampMap) {
      let raw =
        byTimestampMap.get(timestampNum) ||
        byTimestampMap.get(String(timestampNum)) ||
        byTimestampMap.get(String(timestampRaw));
      if (!raw && Array.isArray(datasetMeta?.data)) {
        raw = datasetMeta.data.find((p) => {
          try {
            return p && toTimestampMs(p.ts) === timestampNum;
          } catch (e) {
            return false;
          }
        });
      }
      return raw?.did ?? raw?.ts ?? null;
    }

    if (hasTimestamp && Array.isArray(datasetMeta?.data)) {
      const raw = datasetMeta.data.find((p) => {
        try {
          return p && toTimestampMs(p.ts) === timestampNum;
        } catch (e) {
          return false;
        }
      });
      if (raw) return raw.did ?? raw.ts ?? null;
    }

    if (params.dataIndex != null && Array.isArray(datasetMeta?.data)) {
      const raw = datasetMeta.data[params.dataIndex];
      return raw?.did ?? raw?.ts ?? null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

/**
 * Update the current URL query params with `uid` and optional `did` and navigate.
 *
 * This helper builds a new search string and calls the provided `navigate`
 * function (from `react-router-dom`) with `replace: true` so the change does not
 * create a new history entry.
 *
 * This allows linking directly to a specific device and data point in the UI.
 *
 * @param {number|string} deviceId - Device id to set as `uid`.
 * @param {number|string|null|undefined} dataRowId - Optional data-row id to set as `did`.
 * @param {(to: string, options?: { replace?: boolean }) => void} navigate - `navigate` function from `react-router-dom`.
 * @param {{ pathname: string, search?: string }} location - `location` object (expects `pathname` and `search`).
 * @returns {void}
 */
export const setUidDidInUrl = (deviceId, dataRowId, navigate, location) => {
  if (!deviceId) return;

  if (typeof navigate !== 'function' || !location) {
    console.warn('setUidDidInUrl requires navigate and location; skipping URL update');
    return;
  }

  const sp = new URLSearchParams(location.search);
  sp.set('uid', String(deviceId));
  if (dataRowId != null) sp.set('did', String(dataRowId));
  const newSearch = `?${sp.toString()}`;
  const newUrl = `${location.pathname}${newSearch}`;
  navigate(newUrl, { replace: true });
};
